import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Heading, IconButton, Modal, ModalBody, ModalContent, ModalOverlay, Select, VStack, XOutline } from '@bonusly/design-system';

import { BizySettingsSaveBar } from '../../components/bizy-settings/bizy-settings-save-bar';
import { BizySettingsSecondaryViewHeader, type BizySettingsSecondaryViewHeaderProps } from '../../components/bizy-settings/bizy-settings-secondary-view';
import { ConnectorsSection } from '../../components/bizy-settings/connectors-section';
import { EverboardingSection } from '../../components/bizy-settings/everboarding-section';
import { MemoriesSection } from '../../components/bizy-settings/memories-section';
import { MotionSection } from '../../components/bizy-settings/motion-section';
import { PreferenceSettingRow } from '../../components/bizy-settings/preference-setting-row';
import { ProfileSection } from '../../components/bizy-settings/profile-section';
import { useBizySettingsDraftStore } from '../../components/bizy-settings/store/use-bizy-settings-draft-store';
import { useIsBizyDesktopDownloadEnabled } from '../../components/bizy-side-panel/hooks/use-bizy-desktop-download';
import { getBizyPanelMode } from '../../components/bizy-side-panel/lib/panel-preferences';
import { useBizySidePanelStore, type BizySettingsSection } from '../../components/bizy-side-panel/store/use-bizy-side-panel-store';
import { AppsSection } from './components/apps-section';

const BASE_SETTINGS_SECTIONS: BizySettingsSection[] = ['general', 'memories', 'notifications', 'connections'];
const SETTINGS_MODAL_HEADER_MIN_H = 14;

interface SettingsSectionTabsProps {
	activeSection: BizySettingsSection;
	direction: 'row' | 'column';
	onSelect: (section: BizySettingsSection) => void;
	sections: BizySettingsSection[];
}

const SettingsSectionTabs = ({ activeSection, direction, onSelect, sections }: SettingsSectionTabsProps) => {
	const { t } = useTranslation('bizy_settings');

	return (
		<Flex direction={direction} align="stretch" gap={1}>
			{sections.map((section) => (
				<Button
					key={section}
					variant="ghost"
					bg={activeSection === section ? 'secondary.bg' : 'transparent'}
					color={activeSection === section ? 'secondary.fgOnSurface' : 'text.default'}
					_hover={{
						bg: activeSection === section ? 'secondary.bgHover' : 'secondary.bgAlpha',
						color: activeSection === section ? 'secondary.fgOnSurface' : 'text.default',
					}}
					_active={{
						bg: activeSection === section ? 'secondary.bgActive' : 'secondary.bgAlphaActive',
						color: activeSection === section ? 'secondary.fgOnSurface' : 'text.default',
					}}
					justifyContent="flex-start"
					size="md"
					flexShrink={0}
					fontWeight="medium"
					onClick={() => onSelect(section)}
				>
					{t(`sections.${section}`)}
				</Button>
			))}
		</Flex>
	);
};

const DefaultChatSizeSection = () => {
	const { t } = useTranslation('bizy_settings');
	const draft = useBizySettingsDraftStore((state) => state.pendingDefaultChatSize);
	const setPendingDefaultChatSize = useBizySettingsDraftStore((state) => state.setPendingDefaultChatSize);
	const clearPendingDefaultChatSize = useBizySettingsDraftStore((state) => state.clearPendingDefaultChatSize);

	const currentValue = getBizyPanelMode();
	const displayValue = draft !== null ? draft : currentValue;

	const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
		const nextMode = event.target.value === 'fullscreen' ? 'fullscreen' : 'sidepanel';
		if (nextMode === currentValue) {
			clearPendingDefaultChatSize();
		} else {
			setPendingDefaultChatSize(nextMode);
		}
	};

	return (
		<PreferenceSettingRow label={t('defaultChatSize')} tooltip={t('defaultChatSizeDescription')}>
			<Select flexShrink={0} maxW="180px" onChange={handleChange} size="md" value={displayValue}>
				<option value="sidepanel">{t('defaultChatSizeSidePanel')}</option>
				<option value="fullscreen">{t('defaultChatSizeFullscreen')}</option>
			</Select>
		</PreferenceSettingRow>
	);
};

const SettingsGroupHeader = ({ label }: { label: string }) => (
	<Box pb={3} borderBottom="1px solid" borderColor="border.soft">
		<Heading as="h3" size="md" color="text.default">
			{label}
		</Heading>
	</Box>
);

interface BizySettingsContentProps {
	activeSection: BizySettingsSection;
	onSecondaryViewHeaderChange: (header: BizySettingsSecondaryViewHeaderProps | null) => void;
}

const BizySettingsContent = ({ activeSection, onSecondaryViewHeaderChange }: BizySettingsContentProps) => {
	const { t } = useTranslation('bizy_settings');
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);

	if (activeSection === 'memories') {
		return (
			<Box maxW={isFullscreen ? '720px' : undefined} mx={isFullscreen ? 'auto' : undefined}>
				<MemoriesSection />
			</Box>
		);
	}

	if (activeSection === 'notifications') {
		return (
			<Box maxW={isFullscreen ? '720px' : undefined} mx={isFullscreen ? 'auto' : undefined}>
				<EverboardingSection />
			</Box>
		);
	}

	if (activeSection === 'connections') {
		return (
			<Box maxW={isFullscreen ? '720px' : undefined} mx={isFullscreen ? 'auto' : undefined}>
				<ConnectorsSection onSecondaryViewHeaderChange={onSecondaryViewHeaderChange} />
			</Box>
		);
	}

	if (activeSection === 'apps') {
		return (
			<Box maxW={isFullscreen ? '720px' : undefined} mx={isFullscreen ? 'auto' : undefined}>
				<AppsSection />
			</Box>
		);
	}

	return (
		<VStack spacing={7} align="stretch" maxW={isFullscreen ? '720px' : undefined} mx={isFullscreen ? 'auto' : undefined}>
			<Box>
				<SettingsGroupHeader label={t('groups.personalization')} />
				<ProfileSection />
			</Box>
			<Box>
				<SettingsGroupHeader label={t('groups.preferences')} />
				<DefaultChatSizeSection />
				<MotionSection />
			</Box>
		</VStack>
	);
};

interface BizySettingsModalProps {
	isOpen: boolean;
	onClose: () => void;
	section?: BizySettingsSection;
}

export const BizySettingsModal = ({ isOpen, onClose, section = 'general' }: BizySettingsModalProps) => {
	const { t } = useTranslation('bizy_settings');
	const [activeSection, setActiveSection] = useState<BizySettingsSection>('general');
	const [secondaryViewHeader, setSecondaryViewHeader] = useState<BizySettingsSecondaryViewHeaderProps | null>(null);
	const clearAllDrafts = useBizySettingsDraftStore((state) => state.clearAllDrafts);
	const isAppsTabEnabled = useIsBizyDesktopDownloadEnabled();
	const sections: BizySettingsSection[] = isAppsTabEnabled ? [...BASE_SETTINGS_SECTIONS, 'apps'] : BASE_SETTINGS_SECTIONS;

	useEffect(() => {
		if (isOpen) {
			setActiveSection(section);
			setSecondaryViewHeader(null);
		}
	}, [isOpen, section]);

	const handleSelectSection = (nextSection: BizySettingsSection) => {
		setSecondaryViewHeader(null);
		setActiveSection(nextSection);
	};

	const handleClose = () => {
		clearAllDrafts();
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered scrollBehavior="inside">
			<ModalOverlay bg="bg.overlayBackdrop" />
			<ModalContent
				maxW={{ base: 'calc(100vw - 24px)', md: '900px' }}
				h={{ base: 'calc(100vh - 24px)', md: '640px' }}
				minH={{ base: 'calc(100vh - 24px)', md: '640px' }}
				overflow="hidden"
				borderRadius="2xl"
			>
				<ModalBody p={0} display="flex" flexDirection={{ base: 'column', md: 'row' }} minH={0}>
					<Flex
						w={{ base: '100%', md: '188px' }}
						flexShrink={0}
						direction="column"
						display={{ base: 'none', md: 'flex' }}
						borderRight="1px solid"
						borderColor="border.soft"
						bg="bg.surface"
						px={3}
						pb={4}
					>
						<Flex align="center" flexShrink={0} minH={SETTINGS_MODAL_HEADER_MIN_H} px={2}>
							<Heading as="h1" size="xl" color="text.default">
								{t('title')}
							</Heading>
						</Flex>
						<SettingsSectionTabs activeSection={activeSection} direction="column" onSelect={handleSelectSection} sections={sections} />
					</Flex>
					<Flex direction="column" flex={1} minW={0} minH={0}>
						<Flex align="center" flexShrink={0} justify="space-between" minH={SETTINGS_MODAL_HEADER_MIN_H} px={5}>
							{secondaryViewHeader ? (
								<BizySettingsSecondaryViewHeader {...secondaryViewHeader} />
							) : (
								<Heading as="h2" size="xl" color="text.default">
									{t(`sections.${activeSection}`)}
								</Heading>
							)}
							<IconButton
								aria-label={t('close')}
								icon={<XOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								bg="secondary.bgAlpha"
								borderRadius="full"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlphaHover' }}
								_active={{ bg: 'secondary.bgAlphaActive' }}
								onClick={handleClose}
							/>
						</Flex>
						<Flex display={{ base: 'flex', md: 'none' }} gap={1} overflowX="auto" px={3} pb={3} flexShrink={0}>
							<SettingsSectionTabs activeSection={activeSection} direction="row" onSelect={handleSelectSection} sections={sections} />
						</Flex>
						<Box flex={1} overflowY="auto" px={5} pt={{ base: 4, md: 1 }} pb={4}>
							<BizySettingsContent activeSection={activeSection} onSecondaryViewHeaderChange={setSecondaryViewHeader} />
						</Box>
						{activeSection === 'general' && <BizySettingsSaveBar />}
					</Flex>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
